package gr.tech.site.pages.article

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initWhyyoushouldPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("article/whyyoushould.md", FrontMatterElement.Builder { addScalar("title", "Why You Should Use A Rebate Company for Your Horticulture Energy Rebates and Incentives"); addScalar("description", "At Green Rebates we look at your entire cultivation operation for the complex system of technology and environments that it is and work tirelessly to maximize your rebate potential."); addScalar("author", "Ben Madden"); addScalar("image", "/images/ledgrowlights.jpg"); addScalar("date", "2024-06-24"); addScalar("updated", "2026-02-17"); addList("tags") { addScalar("Big Incentives"); addScalar("Big Rebates"); addScalar("Cost Savings"); addScalar("Energy Efficiency Programs"); addScalar("Energy Incentives"); addScalar("Energy Rebates"); addScalar("Energy-efficient Upgrades"); addScalar("Financial Incentives for Horticulture"); addScalar("Green Rebates"); addScalar("Horticulture Energy Savings"); addScalar("Horticulture Sustainability"); addScalar("Rebates vs. Incentives"); }; }))
}

@Page("/article/why-you-should-use-a-rebate-company-for-your-horticulture-energy-rebates-and-incentives")
@Layout(".components.layouts.ArticleLayout")
@Composable
fun WhyyoushouldPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("why-you-should-use-a-rebate-company-for-your-horticulture-energy-rebates-and-incentives") }) {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Why You Should Use A Rebate Company for Your Horticulture Energy Rebates and Incentives")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("having-a-dedicated-rebate-company-in-your-corner-to-help-you-navigate-the-rebate-process-is-like-hiring-an-accountant-to-do-your-taxes") }) {
        org.jetbrains.compose.web.dom.Text("Having a dedicated Rebate Company in your corner to help you navigate the rebate process is like hiring an accountant to do your taxes.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("At ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://greenrebates.com/") {
            org.jetbrains.compose.web.dom.Text("Green Rebates")
        }
        org.jetbrains.compose.web.dom.Text(" we look at your entire cultivation operation for the complex system of technology and environments that it is and work tirelessly to maximize your rebate potential. We have 60+ years of combined experience in horticulture site design, lighting & environmental control systems, and energy rebate optimization.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("letting-a-lighting-company-handle-your-rebate-may-not-always-be-the-best-choice-and-there-are-several-reasons-for-this") }) {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Letting a lighting company handle your rebate may not always be the best choice, and there are several reasons for this:")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Conflict of Interest:")
        }
        org.jetbrains.compose.web.dom.Text(" Lighting companies are primarily focused on selling their products and services, which may include energy-efficient lighting solutions. When they offer to process rebates for you, there’s a conflict of interest because they have a financial incentive to promote their own products and services over others, even if there are more cost-effective options available to you.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Limited Product Selection:")
        }
        org.jetbrains.compose.web.dom.Text(" Individual lighting companies have a limited range of products that qualify for rebates, and the most cost-effective solution for your situation may not be in their catalog.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Rebate Accuracy:")
        }
        org.jetbrains.compose.web.dom.Text(" Processing rebates can be complex, with specific requirements and documentation needed to qualify. If a lighting company is handling your rebate, there’s a risk that they may make mistakes or miss important details, leading to a delay or denial of your rebate.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Single Product Focus:")
        }
        org.jetbrains.compose.web.dom.Text(" Lighting companies will generally only process rebates for their own lighting products, when rebates exist for a variety of other equipment types, like HVAC and dehumidifier systems. An energy efficiency specialist ensures that you get all the incentives available to you, not just rebates for lighting.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Rebate Optimization:")
        }
        org.jetbrains.compose.web.dom.Text(" It takes an energy efficiency specialist who knows the ins and outs of each of the dozens of individual rebate programs around the country to know how to fully optimize and maximize your rebate submission. It’s a bit like an accountant maximizing tax deductions – a lighting salesman might get your rebate processed, but they will probably be leaving money on the table.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Transparency:")
        }
        org.jetbrains.compose.web.dom.Text(" When a lighting company handles your rebate for you, they may roll part of your rebate into their equipment cost quote, hiding the amount of the rebate.  Without full visibility into the rebate amount, you won’t know how good of a deal you are getting.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Potential Costs:")
        }
        org.jetbrains.compose.web.dom.Text(" Some light companies may charge fees or mark up the cost of products and services when handling rebates. This can reduce the overall financial benefit you receive from the rebate.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Independence and Objectivity:")
        }
        org.jetbrains.compose.web.dom.Text(" When you handle the rebate process independently or work with a neutral third party, you can make decisions based on what’s best for your specific needs and budget, rather than being influenced by a company’s sales tactics.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Compliance and Regulations:")
        }
        org.jetbrains.compose.web.dom.Text(" Rebate programs are subject to various regulations and guidelines, and it’s important to ensure that your rebate application complies with these rules. Lighting companies may not always have the expertise or incentive to navigate these regulations correctly.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("To maximize your rebate benefits, it’s often recommended to research the available rebates yourself, understand the eligibility criteria, and consider working with a reputable, independent energy efficiency consultant or contractor who can provide you with unbiased advice and assistance in securing the rebates you qualify for. This way, you can make informed decisions that align with your energy efficiency goals and financial interests.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("with-over-50-million-dollars-of-rebates-secured-and-over-355-million-kwh-saved-for-our-clients-we-are-your-trusted-advisor-for-all-things-horticulture-electrical-rebates-incentives") }) {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("With over 50 million dollars of rebates secured and over 355 million KWh saved for our clients we are your trusted advisor for all things Horticulture Electrical Rebates & Incentives.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Let Us Show You How Efficiency Pays!")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Use this ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://greenrebates.com") {
            org.jetbrains.compose.web.dom.Text("LINK")
        }
        org.jetbrains.compose.web.dom.Text(" to submit an estimate request and get in touch with our team.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Related:")
        }
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            com.varabyte.kobweb.silk.components.navigation.Link("/article/cash-back-for-growers-a-step-by-step-roadmap-to-utility-incentives") {
                org.jetbrains.compose.web.dom.Text("Cash Back for Growers: A Step-by-Step Roadmap to Utility Incentives")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            com.varabyte.kobweb.silk.components.navigation.Link("/article/rebates-incentives-for-indoor-growers-what-you-need-to-know") {
                org.jetbrains.compose.web.dom.Text("Rebates vs. Incentives for Indoor Growers: What You Need to Know")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            com.varabyte.kobweb.silk.components.navigation.Link("/article/how-energy-incentives-and-green-rebates-are-leading-the-way-in-paving-the-way-for-sustainability-in-the-indoor-ag-space") {
                org.jetbrains.compose.web.dom.Text("How Energy Incentives and Green Rebates are Leading the Way in Paving the Way for Sustainability in the Indoor Ag Space")
            }
        }
    }
}
